import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ANALYTICS_CONSTS,
  AnalyticsUtils,
  API_PATHS,
  apiClient,
  trackGoogleAnalyticsEvent
} from '@cyberint/common-utils';
import { Icons } from '@cyberint/dsc';
import { t } from '../../i18n';
import * as S from './styled';

const { defaultClient } = apiClient;
const { API_V1 } = API_PATHS;

const stopImpersonation = () => defaultClient.post(`${API_V1}/impersonate/stop`, {});

/**
 * Persistent global banner shown while the current session is impersonating a customer.
 *
 * Renders inside the dedicated impersonation tab (opened on the customer's own server), gated purely
 * on the `impersonating` flag from /me's user object, so it shows on every authenticated route — the
 * only cue that this customer-server view is an impersonated session whose actions are real and
 * attributed to the acting Cyberint user. Ending impersonation deletes the session server-side and
 * closes the tab; the acting user's real session lives on in their original tab.
 *
 * Error presentation depends on the host app having a SnackbarProvider, so it's injected via
 * onEndImpersonationError (both the main app and the shell wire a snackbar).
 */
export const ImpersonationBanner = ({ impersonating, customerName, onEndImpersonationError }) => {
  const [isEnding, setIsEnding] = useState(false);

  if (!impersonating) {
    return null;
  }

  const onEndImpersonation = async () => {
    trackGoogleAnalyticsEvent({
      [ANALYTICS_CONSTS.GA_CATEGORY]: ANALYTICS_CONSTS.GA_CATEGORY_SYSTEM,
      [ANALYTICS_CONSTS.GA_ACTION]: 'end customer impersonation',
      [ANALYTICS_CONSTS.META_DATA]: AnalyticsUtils.buildFeatureUsageMetadata({
        event_name: 'feature_interaction',
        feature_area: 'impersonation',
        feature_name: 'customer_impersonation',
        feature_action: 'end_impersonation',
        screen_id: 'impersonation_banner',
        source_surface: 'impersonation_banner'
      })
    });
    setIsEnding(true);
    try {
      await stopImpersonation();
      // The stop tab was script-opened, so close() closes it; if the cookie was re-minted in place
      // instead, close() no-ops on the main tab and the reload re-bootstraps the restored session.
      window.close();
      window.location.reload();
    } catch (e) {
      setIsEnding(false);
      onEndImpersonationError?.(e);
    }
  };

  return (
    <S.Banner data-testid="impersonation-banner">
      <S.Notice>
        <Icons.Visibility size={16} />
        <span>
          <S.ViewLabel>{t('impersonation.customerView')}</S.ViewLabel>
          {` — ${t('impersonation.actingAs', { customerName })}`}
        </span>
      </S.Notice>
      <S.ExitButton
        icon={<Icons.LogOut size={14} />}
        onClick={onEndImpersonation}
        loading={isEnding}
        disabled={isEnding}
        testId="btn-end-impersonation">
        {t('impersonation.exitImpersonation')}
      </S.ExitButton>
    </S.Banner>
  );
};

ImpersonationBanner.propTypes = {
  impersonating: PropTypes.bool,
  customerName: PropTypes.string,
  onEndImpersonationError: PropTypes.func
};
